import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Bell, Check } from 'lucide-react';
import { AuthContext } from '../App';

/**
 * QuickPingReceiver — componente global que polea mensajes "ping rápido"
 * dirigidos al usuario actual y los muestra como modal bloqueante que
 * requiere "Aceptar" para cerrarse. Pensado para avisos urgentes entre
 * admin/profesor (ej: "vamos a correr un deploy en 2 min").
 */
const QuickPingReceiver = () => {
  const { API, user } = useContext(AuthContext);
  const [pending, setPending] = useState([]); // array de pings sin descartar
  const [dismissing, setDismissing] = useState(false);
  const seenIdsRef = useRef(new Set()); // para sonar solo en pings nuevos

  // ── Sonido de notificación ───────────────────────────────
  const playDing = useCallback(() => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'sine';
      o.frequency.setValueAtTime(880, ctx.currentTime);
      o.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12);
      g.gain.setValueAtTime(0.001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
      o.start(ctx.currentTime);
      o.stop(ctx.currentTime + 0.4);
    } catch (_e) { /* noop */ }
  }, []);

  // ── Polling de pendientes ───────────────────────────────
  useEffect(() => {
    if (!user?.id) return;
    let stopped = false;

    const poll = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${API}/pings-rapidos/pendientes`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const list = data?.pings || [];
        if (stopped) return;
        // Detectar pings nuevos para sonar
        const incomingIds = new Set(list.map((p) => p.id));
        const hasNew = list.some((p) => !seenIdsRef.current.has(p.id));
        if (hasNew) playDing();
        seenIdsRef.current = incomingIds;
        setPending(list);
      } catch (_e) { /* silencioso */ }
    };

    poll();
    const intv = setInterval(poll, 8000);
    return () => { stopped = true; clearInterval(intv); };
  }, [API, user?.id, playDing]);

  // ── Aceptar/descartar un ping ───────────────────────────
  const dismiss = async (pingId) => {
    setDismissing(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/pings-rapidos/${pingId}/dismiss`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setPending((prev) => prev.filter((p) => p.id !== pingId));
      seenIdsRef.current.delete(pingId);
    } catch (_e) { /* ignorar */ }
    finally { setDismissing(false); }
  };

  if (!user?.id || pending.length === 0) return null;

  // Mostrar el más antiguo pendiente
  const current = pending[0];
  const sender = current.from_nombre || 'Alguien';
  const senderInitial = sender.trim().slice(0, 1).toUpperCase();
  const fotoSender = current.from_foto;
  const isFromAdmin = current.from_rol === 'admin';

  return createPortal(
    <div
      className="fixed inset-0 z-[2147483640] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
      data-testid="quick-ping-receiver-modal"
      role="alertdialog"
      aria-live="assertive"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md my-auto overflow-hidden max-h-[calc(100dvh-2rem)] flex flex-col animate-in fade-in zoom-in duration-200">
        <div className={`px-5 py-3 flex items-center gap-2 text-white ${isFromAdmin ? 'bg-gradient-to-r from-red-500 to-orange-500' : 'bg-gradient-to-r from-indigo-500 to-purple-600'}`}>
          <Bell className="w-5 h-5 animate-pulse" />
          <h3 className="font-bold text-base">Mensaje urgente</h3>
          {pending.length > 1 && (
            <span className="ml-auto text-xs bg-white/20 rounded-full px-2 py-0.5">
              +{pending.length - 1} más
            </span>
          )}
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <div className="flex items-center gap-3">
            {fotoSender && fotoSender.trim() ? (
              <img
                src={fotoSender}
                alt={sender}
                className="w-14 h-14 rounded-full object-cover ring-2 ring-indigo-400 shrink-0"
              />
            ) : (
              <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl ring-2 ring-indigo-400 shrink-0 ${isFromAdmin ? 'bg-blue-500' : 'bg-orange-500'}`}>
                {senderInitial}
              </div>
            )}
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 truncate">{sender}</p>
              <p className="text-xs text-gray-500 capitalize">{current.from_rol || ''}</p>
            </div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-gray-900 whitespace-pre-wrap break-words text-sm leading-relaxed" data-testid="ping-mensaje-texto">
              {current.mensaje}
            </p>
          </div>
        </div>
        <div className="bg-gray-50 px-5 py-3 border-t flex justify-end shrink-0">
          <button
            onClick={() => dismiss(current.id)}
            disabled={dismissing}
            className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-2 text-sm font-semibold disabled:opacity-50"
            data-testid="ping-aceptar-btn"
            autoFocus
          >
            <Check className="w-4 h-4" />
            Aceptar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default QuickPingReceiver;
